import {useCallback, useEffect, useMemo} from 'react'
import {STORYBOOK, deepMergeAll, logger, tryJson} from 'tizra'
import * as B from '../block'
import {Advanced} from './Advanced'
import {Results} from './Results'
import {
  SearchAdmin,
  SearchGlobalAdmin,
  defaultGlobalConfig,
  defaultTips,
} from './admin'
import {TILDE_ADVANCED, TILDE_RESULTS, useSearchRouter} from './useSearchRouter'
import {useWithinField} from './useWithinField'

const log = logger('quickstart/blocks/SearchBlock')

const PREFIX = 'searchParam-'

const WITHIN_PARAM_NAME = 'within'

export const SearchBlock = B.blockWithInfo(
  {
    name: 'search',
    displayName: 'SearchBlock',
    title: 'Search',
    Admin: SearchAdmin,
    GlobalAdmin: SearchGlobalAdmin,
    defaultGlobalConfig,
    ssrEnabled: false,
  },
  () => {
    const config = B.useGlobalConfig(SearchBlock)

    const customSearchConfig = useMemo(
      () =>
        tryJson<any>(config.customSearchConfigJson, e =>
          log.error('failed to parse customSearchConfigJson', e),
        ),
      [config.customSearchConfigJson],
    )

    const within = useWithinField({
      config: config.fields.within,
      name: WITHIN_PARAM_NAME,
      urlPrefix: PREFIX,
    })

    const searchConfig = B.useSearchConfig(
      deepMergeAll(
        {
          metaTypes: {
            metadata: config.metadataMetaTypes,
          },
          depths: [
            ...(config.metadataMetaTypes.length ? ['metadata'] : []),
            ...(config.tocDepth ? ['toc'] : []),
            ...(config.fulltextDepth ? ['fulltext'] : []),
          ],
          fields: {
            terms: {
              tips: config.fields.terms.customTips || defaultTips,
            },
            // ALWAYS include within, even if not enabled as a facet. That way the
            // Search Within block works, and the user can still remove the
            // constraint with the clickable filter tag. If not enabled as a facet,
            // it will be excluded from order below.
            [WITHIN_PARAM_NAME]: within,
          },
          order: ['terms', 'within'],
        },
        customSearchConfig,
      ),
    )

    const {isRoutedSearch, onAdvancedSearch, switchToAdvanced} =
      useSearchRouter()

    const [urlParams, _setUrlParams] = B.useSearchParams(
      searchConfig.config,
      PREFIX,
    )
    const setUrlParams = useCallback(
      (params: Parameters<typeof _setUrlParams>[0]) =>
        _setUrlParams(params, {
          location: loc => ({
            ...loc,
            pathname: loc.pathname?.replace(
              `/${TILDE_ADVANCED}`,
              `/${TILDE_RESULTS}`,
            ),
          }),
        }),
      [_setUrlParams],
    )

    const search = B.useSearch(
      {
        ...searchConfig,
        ...(isRoutedSearch && {
          urlParams,
          setUrlParams,
        }),
      },
      {
        enabled: !onAdvancedSearch,
        snippet: true,
      },
    )

    // Server sets title to "autotest: Search Results" or "autotest Advanced
    // Search" (yes, one has colon and the other doesn't, so we repair to always
    // include colon). Append the current search terms.
    const siteName = useMemo(() => {
      const siteName =
        typeof document === 'undefined'
          ? undefined
          : document.title.match(
              /^(.*?):*\s+(search result|advanced search).*$/i,
            )?.[1]
      // In storybook, document.title is "blocks-search--site-search"
      // so it won't match above.
      return siteName || (STORYBOOK && 'storybook') || ''
    }, [])
    useEffect(() => {
      if (siteName) {
        const pageName = onAdvancedSearch ? 'Advanced Search' : 'Search Results'
        const forTerms = search.params.terms
          ? ` for ‘${search.params.terms}’`
          : ''
        document.title = `${siteName}: ${pageName}${forTerms}`
      }
    }, [onAdvancedSearch, siteName, search.params.terms])

    return onAdvancedSearch ? (
      <Advanced search={search} />
    ) : (
      <Results
        search={search}
        showPageThumbnails={config.showPageThumbnails}
        switchToAdvanced={switchToAdvanced}
      />
    )
  },
)
